type RygColorValuePair = {
  value: string | null;
  color: 'red' | 'yellow' | 'green' | null;
};

export type FieldSocNutrientApiResponse = {
  field_id: string;
  field_num: number;
  soc_pct: number;
  carbon_stock_tpha: number;
  som_pct: number;
  country: string;
  pH: RygColorValuePair;
  K: RygColorValuePair;
  K_mg_per_l: RygColorValuePair;
  Mg: RygColorValuePair;
  Mg_mg_per_l: RygColorValuePair;
  P_Olsen: RygColorValuePair;
  P_MM: RygColorValuePair;
  Li: RygColorValuePair;
  N: RygColorValuePair;
  Ca: RygColorValuePair;
  Ca_mg_per_l: RygColorValuePair;
};

export type FieldSummaryType = {
  fieldId: string;
  fieldNumber: number;
  socPercentage: number;
  socTPerHa: number;
  somPercentage: number;
  country: string;
  pH: RygColorValuePair;
  k: RygColorValuePair;
  kMpl: RygColorValuePair;
  mg: RygColorValuePair;
  mgMpl: RygColorValuePair;
  pOlsen: RygColorValuePair;
  pMM: RygColorValuePair;
  li: RygColorValuePair;
  n: RygColorValuePair;
  ca: RygColorValuePair;
  caMpl: RygColorValuePair;
};

export type ProjectFarmSummaryApiResponse = {
  farm_id: string;
  farm_name: string;
  farm_area_ha: number;
  core_count: number;
  cores_per_hectare: number;
  soc_pct: number;
  total_carbon_stock_tonnes: number;
  avg_carbon_stock: RygColorValuePair;
};

export type ProjectFarmSummaryType = {
  farmId: string;
  farmName: string;
  farmAreaHa: number;
  coreCount: number;
  coresPerHectare: number;
  socPct: number;
  totalCarbonStockTonnes: number;
  avgCarbonStock: RygColorValuePair;
};

export type ProjectBasicDetailsApiResponse = {
  project_id: string;
  project_name: string;
  csv_downloadable: boolean;
  core_extraction_dates: string;
  sample_analysis_dates: string;
  major_soil_type: string;
  mean_soil_depth_mm: number;
  total_project_area_ha: number;
  total_number_of_fields: number;
  mean_field_area_ha: number;
  total_cores: number;
  cores_per_hectare: number;
  total_carbon_stock_tonnes: number;
  avg_carbon_stock_tpha: number;
  std_dev_carbon_stock_tpha: number;
};

export type ProjectBasicDetailsData = {
  projectId: string;
  projectName: string;
  csvDownloadable: boolean;
  coreExtractionDates: string;
  sampleAnalysisDates: string;
  majorSoilType: string;
  meanSoilDepthMm: number;
  totalProjectAreaHa: number;
  totalNumberOfFields: number;
  meanFieldAreaHa: number;
  totalCores: number;
  coresPerHectare: number;
  totalCarbonStockTonnes: number;
  avgCarbonStockTpha: number;
  stdDevCarbonStockTpha: number;
};

export type NutrientInfoApiResponse = {
  metric: string;
  result: RygColorValuePair;
  legend: string | null;
  comment: string | null;
  optimal_value: number | null;
};

export type FieldNutrientInfo = {
  metric: string;
  result: RygColorValuePair;
  legend: string | null;
  comment: string | null;
  optimal: number | null;
};

export type MultiBarScatterChartData = {
  result_set_1: {
    stock_range_interval: string;
    stock_range_min: string;
    stock_range_max: string;
  }[];
  result_set_2: {
    stock_range: string;
    freq: string;
  }[];
  result_set_3: {
    peer_mean_carbon_stock_tpha: string;
  }[];
  result_set_4: {
    mean_carbon_stock_tpha: string;
  }[];
  result_set_5: {
    fieldId: string;
    peer_group_comment: string;
    peer_population: string;
  }[];
};

export const reportStatus = {
  PENDING: 'pending',
  QUEUED: 'queued',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type ReportStatus = (typeof reportStatus)[keyof typeof reportStatus];

export type ReportStatusData = {
  status: ReportStatus;
  url: string;
};

export type ReportGenerateData = {
  message: string;
};

export type FarmData = {
  status: string;
  csvDownloadable: boolean;
  farmInfo: {
    generalInfo: {
      farmName: string;
      farmId: string;
      projectName: string;
      coreExtractionDates: string;
      sampleAnalysisDates: string;
      farmDescription: string;
    };
    terrainInfo: {
      terrainType: string;
      typicalSoilType: string;
      meanSoilDepth: number | string;
    };
    areaInfo: {
      totalArea: number | string;
      numberOfFields: number | string;
    };
    coresInfo: {
      totalCores: number | string;
      coresPerHectare: number | string;
    };
    fieldCountsByType: Array<{ fieldType: string; count: number }>;
    totalFields: number | null;
  };
  carbonInfo: {
    byFieldType: Array<{
      fieldType: string;
      totalCarbonStock: number;
      averageCarbonPerHectare: number;
      carbonStdDeviation: number;
    }>;
    totalCarbonStock: number | string;
    averageCarbonPerHectare: number | string;
    carbonStdDeviation: number | string;
  };
  breadcrumb: {
    client: { id: string; name: string };
    project: { id: string; name: string };
    farm: { id: string; name: string };
  };
};
